import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { DonorDetails } from '../PublicDonation/types';
import styles from './DonationsAdmin.module.css';

interface DonorResult extends DonorDetails {
  donationsCount: number;
}

interface Props {
  fundId: number;
  onSelect: (donor: Partial<DonorDetails>) => void;
}

const DonorSearchField = ({ fundId, onSelect }: Props) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(id);
  }, [term]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const url = useMemo(
    () => `donations/donors-search/${fundId}?query=${encodeURIComponent(debounced)}`,
    [debounced, fundId],
  );
  const { data, isFetching } = useGetAuthApi<DonorResult[]>(url);

  const handlePick = (donor: DonorResult) => {
    onSelect({
      firstName: donor.firstName,
      lastName: donor.lastName,
      israeliId: donor.israeliId,
      phone: donor.phone?.replace(/\D/g, '') ?? '',
      email: donor.email,
      address: donor.address,
      city: donor.city,
    });
    setTerm(`${donor.firstName} ${donor.lastName}`.trim());
    setOpen(false);
  };

  return (
    <div className={styles.donorSearch} ref={wrapperRef}>
      <input
        type="text"
        value={term}
        placeholder={t('donations.admin.searchDonorPlaceholder')}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className={styles.donorResults}>
          {isFetching && <div className={styles.donorEmpty}>{t('donations.loading')}</div>}
          {!isFetching && (!data || data.length === 0) && (
            <div className={styles.donorEmpty}>{t('donations.admin.noDonorsFound')}</div>
          )}
          {!isFetching && data?.map((donor, idx) => (
            <div key={idx} className={styles.donorResultItem} onClick={() => handlePick(donor)}>
              <span className={styles.donorResultName}>{donor.firstName} {donor.lastName}</span>
              <span className={styles.donorResultMeta}>
                {[donor.israeliId, donor.phone, donor.email].filter(Boolean).join(' · ')}
              </span>
              <span className={styles.donorResultCount}>
                {t('donations.admin.previousDonations', { count: donor.donationsCount })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonorSearchField;
